import React, { ReactNode, useEffect, useState, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useField } from 'react-final-form';
import { useQuery } from '@apollo/client';
import { OwnerOrAgent } from '../../components/OwnerOrAgent';
import IconCheck from '../../../../../public/static/images/new_ad/icons/icon-check.svg';
import { FORM_VALUES } from '../../../../../utils/helpers/constants';
import { InputTypeMap } from '../../../../ui/Input';
import { FormBlockWrapper } from '../FormBlockWrapper';
import { FormTextInput } from '../../components/FormTextInput';
import { Disclaimer, PhoneFieldHint } from './styled';
import { Typography } from '../../../../ui/Typography';
import { CompanyTargetTypeEnum } from '../../../../../graphql/types';
import { userDataQuery } from '../../../../../api/queries';
import { UserDataQuery } from '../../../../../graphql/graphql';

interface ContactsProps {
  termsBlock: ReactNode | boolean;
  isEdit?: boolean;
}

const ContactsComponent: React.FC<ContactsProps> = ({
  termsBlock,
  isEdit = false
}) => {
  const { change } = useForm();
  const { t } = useTranslation();
  const { input: phoneField } = useField(FORM_VALUES.phone, {
    subscription: { value: true }
  });
  const [touched, setTouched] = useState(false);

  const { data } = useQuery<UserDataQuery>(userDataQuery, {
    ssr: false
  });

  const profile = data?.profile;
  const isAgency = profile?.targetType === CompanyTargetTypeEnum.Agency;
  const isResidence = profile?.targetType === CompanyTargetTypeEnum.Residence;

  useEffect(() => {
    if (profile?.phone && !phoneField.value && !isEdit) {
      change(FORM_VALUES.name, profile?.name);
      change(FORM_VALUES.phone, profile?.phone);
      change(FORM_VALUES.email, profile?.email);
      if (isAgency) {
        change(FORM_VALUES.owner, 'AGENT');
      }
    }
  }, [profile, phoneField.value, isEdit, change, isAgency]);

  const disableNameEditing = !!profile?.name && !isEdit;

  return (
    <FormBlockWrapper header={t('new_ad.contacts')} required>
      {!isAgency && !isResidence && !isEdit && <OwnerOrAgent />}

      <FormTextInput
        name={FORM_VALUES.name}
        placeholder={t('new_ad.name')}
        isEnabledAutoSelectionRange={false}
        icon={disableNameEditing ? IconCheck : null}
        disabled={disableNameEditing}
        hasErrorField
      />

      {!isEdit && (
        <>
          <FormTextInput
            name={FORM_VALUES.email}
            placeholder={t('new_ad.email')}
            disabled={Boolean(profile?.email)}
            icon={profile?.email ? IconCheck : null}
            isEnabledAutoSelectionRange={false}
            hasErrorField
          />

          <div>
            <FormTextInput
              name={FORM_VALUES.phone}
              type={InputTypeMap.phone}
              disabled={Boolean(profile)}
              placeholder={
                profile ? t('new_ad.phone_auth_user') : t('new_ad.phone')
              }
              onClick={() => setTouched(true)}
              icon={profile?.phone ? IconCheck : null}
              hasErrorField
              showPhoneMask
            />

            {touched && !profile && (
              <>
                <PhoneFieldHint tag="body_2">
                  {t('new_ad.phone_hint')}
                </PhoneFieldHint>

                <Disclaimer data-cy="item-form-disclaimer">
                  <Typography tag="caption_1">
                    {t('new_ad.disclaimer')}
                  </Typography>
                </Disclaimer>
              </>
            )}
          </div>
        </>
      )}

      {termsBlock}
    </FormBlockWrapper>
  );
};

export const Contacts = memo(ContactsComponent);
